import React from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SipocItem {
  name: string;
  description?: string;
}

interface SipocDiagramProps {
  title?: string;
  suppliers: string[] | SipocItem[];
  inputs: string[] | SipocItem[];
  process: string[] | SipocItem[];
  outputs: string[] | SipocItem[];
  customers: string[] | SipocItem[];
}

// ---------------------------------------------------------------------------
// Column configuration
// ---------------------------------------------------------------------------

interface ColumnConfig {
  key: keyof Pick<SipocDiagramProps, 'suppliers' | 'inputs' | 'process' | 'outputs' | 'customers'>;
  letter: string;
  label: string;
  bg: string;
  bgLight: string;
  textAccent: string;
  numbered: boolean;
}

const COLUMNS: ColumnConfig[] = [
  { key: 'suppliers', letter: 'S', label: 'Suppliers',  bg: 'bg-blue-500',    bgLight: 'bg-blue-50',    textAccent: 'text-blue-600',    numbered: false },
  { key: 'inputs',    letter: 'I', label: 'Inputs',     bg: 'bg-cyan-500',    bgLight: 'bg-cyan-50',    textAccent: 'text-cyan-600',    numbered: false },
  { key: 'process',   letter: 'P', label: 'Process',    bg: 'bg-accent-500',  bgLight: 'bg-accent-50',  textAccent: 'text-accent-600',  numbered: true },
  { key: 'outputs',   letter: 'O', label: 'Outputs',    bg: 'bg-emerald-500', bgLight: 'bg-emerald-50', textAccent: 'text-emerald-600', numbered: false },
  { key: 'customers', letter: 'C', label: 'Customers',  bg: 'bg-purple-500',  bgLight: 'bg-purple-50',  textAccent: 'text-purple-600',  numbered: false },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Normalise a mixed array of strings | SipocItem into SipocItem[]. */
function normaliseItems(items: string[] | SipocItem[]): SipocItem[] {
  return items.map((item) =>
    typeof item === 'string' ? { name: item } : item,
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Right-pointing arrow rendered between desktop columns. */
const ColumnArrow: React.FC = () => (
  <div className="hidden md:flex items-center justify-center" aria-hidden="true">
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className="w-5 h-5 text-gray-300"
    >
      <path
        d="M7 4l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

/** A single item row inside a column. */
const ItemRow: React.FC<{
  item: SipocItem;
  index: number;
  numbered: boolean;
  textAccent: string;
}> = ({ item, index, numbered, textAccent }) => (
  <li className="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-b-0">
    {numbered ? (
      <span
        className={`flex-shrink-0 w-5 h-5 rounded-full ${textAccent} bg-white border border-current text-[10px] font-bold flex items-center justify-center mt-0.5`}
      >
        {index + 1}
      </span>
    ) : (
      <span className={`flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full ${textAccent.replace('text-', 'bg-')}`} />
    )}
    <div className="min-w-0">
      <span className={`text-xs font-medium text-gray-800 leading-tight ${item.description ? 'block' : ''}`}>
        {item.name}
      </span>
      {item.description && (
        <span className="block text-[11px] text-gray-500 leading-snug mt-0.5">
          {item.description}
        </span>
      )}
    </div>
  </li>
);

/** A full SIPOC column (desktop). */
const DesktopColumn: React.FC<{
  config: ColumnConfig;
  items: SipocItem[];
  isLast: boolean;
}> = ({ config, items, isLast }) => (
  <div className={`flex flex-col min-w-0 ${!isLast ? 'border-r border-gray-200' : ''}`}>
    {/* Colored header strip */}
    <div className={`${config.bg} px-3 py-4 text-center`}>
      <div className="text-2xl font-extrabold text-white leading-none">
        {config.letter}
      </div>
      <div className="text-[11px] font-semibold text-white/90 uppercase tracking-wider mt-1">
        {config.label}
      </div>
    </div>

    {/* Items list */}
    <div className="px-3 py-3 flex-grow">
      <ul className="list-none m-0 p-0">
        {items.map((item, i) => (
          <ItemRow
            key={i}
            item={item}
            index={i}
            numbered={config.numbered}
            textAccent={config.textAccent}
          />
        ))}
      </ul>
    </div>
  </div>
);

/** A mobile card for a single SIPOC section. */
const MobileCard: React.FC<{
  config: ColumnConfig;
  items: SipocItem[];
}> = ({ config, items }) => (
  <div className="flex rounded-lg border border-gray-200 overflow-hidden">
    {/* Colored letter badge */}
    <div
      className={`${config.bg} flex flex-col items-center justify-center px-4 py-3 flex-shrink-0`}
    >
      <span className="text-xl font-extrabold text-white leading-none">
        {config.letter}
      </span>
      <span className="text-[9px] font-semibold text-white/90 uppercase tracking-wider mt-0.5">
        {config.label}
      </span>
    </div>

    {/* Items */}
    <div className="px-3 py-2 flex-grow min-w-0">
      <ul className="list-none m-0 p-0">
        {items.map((item, i) => (
          <ItemRow
            key={i}
            item={item}
            index={i}
            numbered={config.numbered}
            textAccent={config.textAccent}
          />
        ))}
      </ul>
    </div>
  </div>
);

/** Down-pointing arrow between mobile cards. */
const MobileArrow: React.FC = () => (
  <div className="flex md:hidden items-center justify-center py-0.5" aria-hidden="true">
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className="w-5 h-5 text-gray-300"
    >
      <path
        d="M4 7l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SipocDiagram: React.FC<SipocDiagramProps> = ({
  title,
  suppliers,
  inputs,
  process,
  outputs,
  customers,
}) => {
  const data: Record<string, SipocItem[]> = {
    suppliers: normaliseItems(suppliers),
    inputs: normaliseItems(inputs),
    process: normaliseItems(process),
    outputs: normaliseItems(outputs),
    customers: normaliseItems(customers),
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden my-8">
      {/* Optional title header */}
      {title && (
        <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-800 m-0">
            {title}
          </h3>
        </div>
      )}

      {/* ----- Desktop: 5-column grid with arrows between columns ----- */}
      <div className="hidden md:block">
        {/* Grid with 5 data columns and 4 arrow gutters */}
        <div
          className="grid"
          style={{
            gridTemplateColumns: '1fr auto 1fr auto 1fr auto 1fr auto 1fr',
          }}
        >
          {COLUMNS.map((col, i) => (
            <React.Fragment key={col.key}>
              <DesktopColumn
                config={col}
                items={data[col.key]}
                isLast={i === COLUMNS.length - 1}
              />
              {i < COLUMNS.length - 1 && <ColumnArrow />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ----- Mobile: stacked cards ----- */}
      <div className="md:hidden p-4 space-y-1">
        {COLUMNS.map((col, i) => (
          <React.Fragment key={col.key}>
            <MobileCard config={col} items={data[col.key]} />
            {i < COLUMNS.length - 1 && <MobileArrow />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default SipocDiagram;
